// src/components/HotkeysModal.js
//
// The keyboard reference, shown over the deck rather than as a separate page.
//
// The app has no backend — everything lives in browser memory until you export
// — so navigating away from the editor to read a help page risks the work in
// progress. An overlay keeps the deck loaded underneath and closes on Escape.
//
// Every shortcut listed here is read off the real handlers: the global keys in
// LessonTemplate's keydown effect, the deck history handler, and the Slide
// Designer stage handler. If a shortcut changes there, change it here too.

import React, { useEffect, useRef } from 'react';
import { X, Keyboard } from 'lucide-react';
import { BLOCK_SHORTCUTS, chordsFor, comboFor } from '../utils/blockShortcuts';

const GROUPS = [
  {
    title: 'Working with the deck',
    hint: 'Available whenever you are not typing in a field or an editor.',
    keys: [
      { combo: ['E'], label: 'Switch between Edit and Preview' },
      { combo: ['P'], label: 'Go to Preview' },
      { combo: ['F'], label: 'Full-screen preview on / off' },
      { combo: ['Esc'], label: 'Leave full-screen preview' },
      { combo: ['['], label: 'Show or hide the Sections drawer (left)' },
      { combo: [']'], label: 'Show or hide the Insert drawer (right)' },
      { combo: ['Ctrl', 'C'], label: 'Copy the current slide' },
      { combo: ['Ctrl', 'X'], label: 'Cut the current slide' },
      { combo: ['Ctrl', 'V'], label: 'Paste a slide after the current slide' },
      { combo: ['Ctrl', 'Shift', 'N'], label: 'Insert a new blank slide — empty, no text box' },
      // On the outline row, not the page: the canvas underneath answers Delete
      // with its own selected objects. See handleSlideRowKeyDown.
      { combo: ['Del'], label: 'Delete the slide whose row is focused in the Sections drawer (Ctrl+Z undoes it)' },
      { combo: ['?'], label: 'Open this shortcut list' }
    ]
  },
  {
    title: 'Saving',
    hint: 'These two work even while you are typing, the way Ctrl+S does everywhere else.',
    keys: [
      { combo: ['Ctrl', 'S'], label: 'Save the deck' },
      { combo: ['Ctrl', 'Alt', 'S'], label: 'Save + — save and bump the version' }
    ]
  },
  {
    title: 'Insert a block',
    hint: 'In Edit mode, with the slide focused rather than a text editor. Each one opens the same panel as the matching button in the Insert drawer.',
    // Built from the same table the key handler reads — including each entry's
    // own modifiers, so an entry that has had to leave the Ctrl+Alt row is
    // printed as it actually is, and an entry that answers to more than one
    // chord gets a row for each. Nothing here can advertise a key the handler
    // does not answer. See src/utils/blockShortcuts.js.
    keys: BLOCK_SHORTCUTS.flatMap((shortcut) => chordsFor(shortcut).map((entry, index) => ({
      combo: comboFor(entry),
      label: index === 0 ? shortcut.label : `${shortcut.label} — or this, if the first is taken`
    })))
  },
  {
    title: 'Undo history',
    hint: 'Applies to the deck. Inside a text editor, that editor’s own history wins.',
    keys: [
      { combo: ['Ctrl', 'Z'], label: 'Undo' },
      { combo: ['Ctrl', 'Shift', 'Z'], label: 'Redo' },
      { combo: ['Ctrl', 'Y'], label: 'Redo (alternative)' }
    ]
  },
  {
    title: 'Presenting',
    hint: 'While previewing or in full screen.',
    keys: [
      { combo: ['↓'], label: 'Next slide' },
      { combo: ['Space'], label: 'Next slide' },
      { combo: ['↑'], label: 'Previous slide' },
      { combo: ['←'], label: 'Nudge the slide left (full screen)' },
      { combo: ['→'], label: 'Nudge the slide right (full screen)' },
      { combo: ['H'], label: 'Hide or show the full-screen controls' }
    ]
  },
  {
    title: 'Slide Designer',
    hint: 'With the slide canvas focused and an object selected.',
    keys: [
      { combo: ['Ctrl', 'A'], label: 'Select every object on the slide' },
      { combo: ['Ctrl', 'C'], label: 'Copy selection' },
      { combo: ['Ctrl', 'X'], label: 'Cut selection' },
      { combo: ['Ctrl', 'V'], label: 'Paste' },
      { combo: ['Ctrl', 'G'], label: 'Group selection' },
      { combo: ['Ctrl', 'Shift', 'G'], label: 'Ungroup' },
      { combo: ['Enter'], label: 'Edit the selected text in place' },
      { combo: ['F2'], label: 'Edit the selected text in place' },
      { combo: ['Delete'], label: 'Delete selection' },
      { combo: ['←↑→↓'], label: 'Nudge by 1px' },
      { combo: ['Shift', '←↑→↓'], label: 'Nudge by 4px' },
      { combo: ['Esc'], label: 'Clear the selection' }
    ]
  }
];

const Key = ({ children }) => (
  <kbd className="hotkeys__key">{children}</kbd>
);

const HotkeysModal = ({ isOpen, onClose }) => {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    closeRef.current?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="hotkeys__backdrop"
      // data-app-modal is what the deck's undo handler looks for so Ctrl+Z does
      // not quietly rewrite the deck behind an open dialog.
      data-app-modal
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={onClose}
    >
      <div className="hotkeys" onClick={(event) => event.stopPropagation()}>
        <div className="hotkeys__header">
          <h2 className="hotkeys__title">
            <Keyboard size={18} aria-hidden="true" />
            Keyboard shortcuts
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="hotkeys__close"
            onClick={onClose}
            aria-label="Close keyboard shortcuts"
          >
            <X size={18} />
          </button>
        </div>

        <p className="hotkeys__note">
          On a Mac use <Key>&#8984;</Key> wherever <Key>Ctrl</Key> is shown.
        </p>

        <div className="hotkeys__groups">
          {GROUPS.map((group) => (
            <section key={group.title} className="hotkeys__group">
              <h3 className="hotkeys__group-title">{group.title}</h3>
              <p className="hotkeys__group-hint">{group.hint}</p>
              <ul className="hotkeys__list">
                {group.keys.map((entry) => (
                  <li key={`${group.title}-${entry.label}-${entry.combo.join('+')}`} className="hotkeys__row">
                    <span className="hotkeys__combo">
                      {entry.combo.map((key, index) => (
                        <React.Fragment key={key}>
                          {index > 0 && <span className="hotkeys__plus">+</span>}
                          <Key>{key}</Key>
                        </React.Fragment>
                      ))}
                    </span>
                    <span className="hotkeys__label">{entry.label}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HotkeysModal;
